
import localization from '~/mixins/localization'

export default {
  mixins: [localization],

  props: {
    title: {
      type: String,
      default: null,
    },

    subtitle: {
      type: String,
      default: null,
    },

    text: {
      type: String,
      default: null,
    },

    form: {
      type: Object,
      default: null,
    },

    privacyPolicyCheckboxLabel: {
      type: String,
      default: null,
    },

    hideTextareaLabel: {
      type: Boolean,
      default: false,
    },

    isEventForm: {
      type: Boolean,
      default: false,
    },

    sendButtonText: {
      type: String,
      default: null,
    },

    event: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      state: null,
      submitted: false,
      formFields: [],
      formData: {
        honeypot: null,
      },

      errors: [],
      hasFullyBookedError: false,
    }
  },

  async fetch() {
    const { data } = await this.$axios.$get(`/api/forms/${this.form.handle}`)

    this.formFields = Object.values(data.fields)
  },

  computed: {
    /**
     * Map form fields with form field rules
     * since Statamic returns them separately
     */
    fields() {
      if (!this.form) return []

      const fields = []

      this.form.fields.forEach((field, index) => {
        fields.push({
          ...field,
          validate: this.formFields[index]?.validate,
          rules: this.mergedRules(
            this.form.rules[field.handle],
            this.formFields[index]?.validate
          ),
          width: this.getGridColSpanBy(this.formFields[index]?.width),
          if: this.formFields[index]?.if,
        })
      })

      if (this.showPrivacyPolicyCheckbox) {
        fields.push({
          handle: 'terms',
          type: 'terms-checkbox',
          display: this.$t('form.terms-label'),
          rules: ['required'],
          config: {
            options: {
              yes: this.$t('form.terms-yes-part-1'),
            },
          },
        })
      }

      return fields
    },
  },

  mounted() {
    this.defineFormData()
  },

  methods: {
    defineFormData() {
      if (!this.form) return

      this.form.fields.forEach((field) => {
        if (field.type === 'checkboxes' || field.type === 'terms-checkbox') {
          this.formData[field.handle] = []
        } else {
          this.formData[field.handle] = ''
        }
      })
    },

    /**
     * Initialize form data with form field values
     * before sending a request to the server
     */
    initFormData() {
      const formData = new FormData()

      this.fields.forEach((field) => {
        const fieldValue = this.formData[field.handle]

        if (
          fieldValue === '' ||
          fieldValue === null ||
          fieldValue === undefined
        ) {
          return
        }

        if (Array.isArray(fieldValue)) {
          fieldValue.forEach((value) =>
            formData.append(`${field.handle}[]`, value)
          )
        } else {
          formData.append(field.handle, fieldValue)
        }
      })

      if (this.isEventForm) {
        formData.append('form_handle', this.form.handle)
        formData.append('event_page_path', this.$route.path)
      }

      if (formData.get('source') === null) {
        formData.append('source', window.location.href)
      }

      return formData
    },

    async submit() {
      this.state = 'loading'

      if (this.intersectSubmission) {
        this.$emit('submit', this.initFormData())
      }

      try {
        await this.$axios.$post(
          `!/forms/${this.form.handle}`,
          this.initFormData(),
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Requested-With': 'XMLHttpRequest',
            },
          }
        )

        if (this.$gtag) {
          this.$gtag.event('submit_form', {
            event_category: 'form_submission',
            event_label: this.form.handle,
          })
        }

        // Wait until the models are updated in the UI
        this.$nextTick(() => {
          this.$refs.form.reset()

          this.submitted = true

          this.$emit('submitted')

          this.state = null
        })
      } catch (e) {
        this.state = 'error'

        this.$sentry.captureException(e)

        this.handleError(e)
      }
    },

    handleError(e) {
      if (!e.response) {
        console.error(e)
        return
      }

      // only handle 400 errors for now
      if (e.response.status !== 400) return

      this.errors = e.response.data.errors

      // check if error contains topic fully booked error)
      if (
        Object.entries(e.response.data.error).some((e) =>
          e[0].includes('topic_fully_booked')
        )
      ) {
        this.hasFullyBookedError = true
      }
    },

    mergedRules(rules, validate) {
      const mergedRules = [
        ...new Set([
          ...(Array.isArray(rules) ? rules : []),
          ...(Array.isArray(validate) ? validate : []),
        ]),
      ]

      // use only strings
      return mergedRules.filter((rule) => typeof rule === 'string')
    },

    validVeeRules(rules) {
      if (!rules) {
        return ''
      }

      return rules.filter((r) => r !== 'nullable' && r !== 'array').join('|')
    },

    getGridColSpanBy(width) {
      switch (width) {
        case 25:
          return 'col-span-12 sm:col-span-3'
        case 33:
          return 'col-span-12 sm:col-span-4'
        case 50:
          return 'col-span-12 sm:col-span-6'
        case 66:
          return 'col-span-12 sm:col-span-8'
        case 75:
          return 'col-span-12 sm:col-span-9'
        case 100:
          return 'col-span-12 sm:col-span-12'
      }
    },

    condition(field) {
      if (!field.if && !field.if_any && !field.unless && !field.unless_any) {
        return true
      }

      if (field.if) {
        const conditions = field.if
        return Object.keys(conditions).every((field) =>
          this.testCondition(field, conditions[field])
        )
      } else if (field.if_any) {
        const conditions = field.if_any
        return Object.keys(conditions).some((field) =>
          this.testCondition(field, conditions[field])
        )
      } else if (field.unless) {
        const conditions = field.unless
        return !Object.keys(conditions).every((field) =>
          this.testCondition(field, conditions[field])
        )
      } else if (field.unless_any) {
        const conditions = field.unless_any
        return !Object.keys(conditions).some((field) =>
          this.testCondition(field, conditions[field])
        )
      }

      return false
    },

    testCondition(field, condition) {
      const testField = this.fields.find((f) => f.handle === field)

      if (!testField) {
        console.error(`field ${field} not found, checking condition failed!`)

        return false
      }

      const [, operator, testForValue] = condition.match(/^([^\s]+) (.+)/)

      switch (operator) {
        case 'equals':
        case '===':
          return this.formData[field] === testForValue
        case 'not':
        case '!==':
          return this.formData[field] !== testForValue
        case 'contains':
          return this.formData[field]?.includes(testForValue)
        case 'contains_any':
          return testForValue
            ?.split(',')
            .some((v) => this.formData[field]?.includes(v))
        case '>':
          return this.formData[field] > testForValue
        case '>=':
          return this.formData[field] >= testForValue
        case '<':
          return this.formData[field] < testForValue
        case '<=':
          return this.formData[field] <= testForValue
        default:
          console.error(
            `operator ${operator} not supported, checking condition failed!`
          )
          return false
      }
    },
  },
}
